import React, { useState, useEffect } from 'react';
import { Card, Select, Button, message, Spin, Descriptions, Input, Space, Tooltip, Modal, Form, Divider, Alert, Tag, Row, Col } from 'antd';
import { UserOutlined, PhoneOutlined, WechatOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, TranslationOutlined } from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';

const { TextArea } = Input;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const StepAucklandMeeting = ({ tour, tourNotice, tourists = [], onRefresh }) => {
  const [staffList, setStaffList] = useState([]);
  const [selectedGuideId, setSelectedGuideId] = useState(tour?.guideId || null);
  const [localGuides, setLocalGuides] = useState(tour?.localGuides || []);
  const [meetingNotes, setMeetingNotes] = useState(tourNotice?.aucklandMeetingInfo || '');
  const [meetingNotesEn, setMeetingNotesEn] = useState(tourNotice?.aucklandMeetingInfoEn || '');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createGuideVisible, setCreateGuideVisible] = useState(false);
  const [creatingGuide, setCreatingGuide] = useState(false);
  const [guideForm] = Form.useForm();

  // 规格统计：Plus款（with_tour）含领队服务，基础款（no_tour）不含
  const plusTourists = tourists.filter(t => t.packageType === 'with_tour');
  const basicTourists = tourists.filter(t => t.packageType === 'no_tour');
  const allBasic = tourists.length > 0 && basicTourists.length === tourists.length;

  useEffect(() => {
    fetchStaffList();
  }, []);

  useEffect(() => {
    setSelectedGuideId(tour?.guideId || null);
    setLocalGuides(tour?.localGuides || []);
  }, [tour?.guideId, tour?.localGuides]);

  useEffect(() => {
    setMeetingNotes(tourNotice?.aucklandMeetingInfo || '');
    setMeetingNotesEn(tourNotice?.aucklandMeetingInfoEn || '');
  }, [tourNotice?.aucklandMeetingInfo, tourNotice?.aucklandMeetingInfoEn]);

  const fetchStaffList = async () => {
    try {
      const res = await axios.get(`${API_BASE}/salespersons`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        const guides = (res.data.data || []).filter(s => s.role === 'guide');
        setStaffList(guides);
      }
    } catch (err) {
      message.error('获取员工列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslateNotes = async () => {
    if (!meetingNotes || !meetingNotes.trim()) {
      message.warning('请先填写中文备注');
      return;
    }
    try {
      setTranslating(true);
      const res = await axios.post(`${API_BASE}/translate`, { text: meetingNotes, targetLang: 'en' }, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) {
        setMeetingNotesEn(res.data.data.translation);
        message.success('翻译成功');
      }
    } catch (err) {
      message.error('翻译失败');
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // 过滤掉完全空白的导游条目
      const validGuides = localGuides.filter(g => g.name || g.phone || g.note);

      await axios.put(
        `${API_BASE}/tours/${tour.id}/guide`,
        {
          guideId: selectedGuideId,
          localGuides: validGuides.length > 0 ? validGuides : null,
        },
        { headers: AuthService.getAuthHeaders() }
      );

      // 保存备注到通知书
      await axios.put(
        `${API_BASE}/tour-notices/tour/${tour.id}`,
        {
          aucklandMeetingInfo: meetingNotes || null,
          aucklandMeetingInfoEn: meetingNotesEn || null,
        },
        { headers: AuthService.getAuthHeaders() }
      );

      message.success('保存成功');
      await onRefresh();
    } catch (err) {
      message.error('保存失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 快速新建领队（role=guide 员工）
  const handleCreateGuide = async () => {
    let values;
    try {
      values = await guideForm.validateFields();
    } catch (e) {
      return;
    }
    try {
      setCreatingGuide(true);
      const res = await axios.post(
        `${API_BASE}/salespersons`,
        { ...values, role: 'guide' },
        { headers: AuthService.getAuthHeaders() }
      );
      if (res.data.success) {
        message.success('领队创建成功');
        setCreateGuideVisible(false);
        guideForm.resetFields();
        await fetchStaffList();
        const newId = res.data.data?.id;
        if (newId) setSelectedGuideId(newId);
      }
    } catch (err) {
      message.error('创建失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingGuide(false);
    }
  };

  const addGuide = () => {
    setLocalGuides([...localGuides, { name: '', phone: '', note: '' }]);
  };

  const removeGuide = (index) => {
    setLocalGuides(localGuides.filter((_, i) => i !== index));
  };

  const updateGuide = (index, field, value) => {
    setLocalGuides(localGuides.map((g, i) => i === index ? { ...g, [field]: value } : g));
  };

  const selectedGuide = staffList.find(s => s.id === selectedGuideId);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  return (
    <div>
      <Card title="领队选择" style={{ marginBottom: 16 }}>
        {allBasic ? (
          <Alert
            type="info"
            showIcon
            message="本团游客均为基础款，无需选择领队"
            style={{ marginBottom: 12 }}
          />
        ) : (plusTourists.length > 0 || basicTourists.length > 0) && (
          <Alert
            type={plusTourists.length > 0 && !selectedGuideId ? 'warning' : 'info'}
            showIcon
            message={
              <Space size={8}>
                <span>本团规格：</span>
                {plusTourists.length > 0 && <Tag color="blue" style={{ margin: 0 }}>Plus款 {plusTourists.length} 人</Tag>}
                {basicTourists.length > 0 && <Tag style={{ margin: 0 }}>基础款 {basicTourists.length} 人</Tag>}
              </Space>
            }
            style={{ marginBottom: 12 }}
          />
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: selectedGuide ? 16 : 0 }}>
          <Select
            style={{ flex: 1, maxWidth: 400 }}
            placeholder="请选择领队"
            value={selectedGuideId}
            onChange={(val) => setSelectedGuideId(val)}
            allowClear
            showSearch
            optionFilterProp="label"
            options={staffList.map(s => ({
              value: s.id,
              label: s.name,
            }))}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  style={{ paddingLeft: 12 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setCreateGuideVisible(true)}
                >
                  新建领队
                </Button>
              </>
            )}
          />
        </div>

        {selectedGuide && (
          <Card type="inner" title="领队信息" size="small">
            <Descriptions column={{ xs: 1, sm: 3 }} size="small">
              <Descriptions.Item label={<><UserOutlined /> 姓名</>}>
                {selectedGuide.name}
              </Descriptions.Item>
              <Descriptions.Item label={<><PhoneOutlined /> 电话</>}>
                {selectedGuide.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={<><WechatOutlined /> 微信</>}>
                {selectedGuide.wechat || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Card>

      <Card
        title="导游信息"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="dashed" icon={<PlusOutlined />} onClick={addGuide} size="small">
            添加导游
          </Button>
        }
      >
        {localGuides.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: '16px 0' }}>
            暂无导游信息，点击右上角"添加导游"
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {localGuides.map((guide, index) => (
              <div key={index} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Input
                  style={{ flex: 1 }}
                  placeholder="姓名"
                  value={guide.name}
                  onChange={(e) => updateGuide(index, 'name', e.target.value)}
                  prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                />
                <Input
                  style={{ flex: 1 }}
                  placeholder="电话"
                  value={guide.phone}
                  onChange={(e) => updateGuide(index, 'phone', e.target.value)}
                  prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                />
                <Input
                  style={{ flex: 2 }}
                  placeholder="备注"
                  value={guide.note}
                  onChange={(e) => updateGuide(index, 'note', e.target.value)}
                />
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeGuide(index)}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="备注" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>备注（中文）</div>
            <TextArea
              rows={4}
              placeholder="集合地点、出发时间、特殊说明等..."
              value={meetingNotes}
              onChange={(e) => setMeetingNotes(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>
              备注（English）{' '}
              <Tooltip title="翻译到英文">
                <Button type="primary" size="small" icon={<TranslationOutlined />} loading={translating} onClick={handleTranslateNotes} />
              </Tooltip>
            </div>
            <TextArea
              rows={4}
              placeholder="Meeting point, departure time, special instructions..."
              value={meetingNotesEn}
              onChange={(e) => setMeetingNotesEn(e.target.value)}
            />
          </Col>
        </Row>
      </Card>

      <div style={{ textAlign: 'right' }}>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          保存
        </Button>
      </div>

      <Modal
        title="新建领队"
        open={createGuideVisible}
        onCancel={() => setCreateGuideVisible(false)}
        onOk={handleCreateGuide}
        confirmLoading={creatingGuide}
        okText="创建"
      >
        <Form form={guideForm} layout="vertical" initialValues={{ password: 'wanguo2026' }}>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="领队姓名" />
          </Form.Item>
          <Form.Item name="username" label="登录用户名" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input placeholder="用于登录系统的用户名" />
          </Form.Item>
          <Form.Item name="password" label="初始密码" rules={[{ required: true, message: '请输入初始密码' }]}>
            <Input placeholder="初始密码" />
          </Form.Item>
          <Form.Item name="phone" label="电话">
            <Input placeholder="联系电话" />
          </Form.Item>
          <Form.Item name="wechat" label="微信">
            <Input placeholder="微信号" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StepAucklandMeeting;
